import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { 
  ArrowLeft, 
  Settings, 
  Signal,
  SignalLow,
  SignalMedium,
  SignalHigh,
  Volume2,
  VolumeX,
  Footprints,
  ListChecks,
  ChevronRight,
  X,
  Navigation,
  MessageCircle,
  Sparkles,
} from "lucide-react";
import { SAFE_AREA } from "@/utils/safeArea";
import { PlatformMap } from "@/components/PlatformMap";
import logoLight from "@/assets/straiv-logo-light.png";
import logoDark from "@/assets/straiv-logo-dark.png";
import { useTheme } from "next-themes";
import { Geolocation } from "@capacitor/geolocation";
import { Capacitor } from "@capacitor/core";
import {
  prefetchCoachAudio,
  speakNativeNow,
  stopCoachVoice,
} from "@/lib/naturalVoice";
import { getHealthConnectedState } from "@/lib/appleHealth";
import { useAuth } from "@/hooks/useAuth";
import { parseRunPlan } from "@/lib/runPlanParser";
import { structureRunGuideForCoach } from "@/lib/runGuideStructure";
import { HeartPulse, Info } from "lucide-react";
import { toast } from "sonner";
import { App } from "@capacitor/app";
import { Keyboard } from "@capacitor/keyboard";
import { ensureLocationForOutdoorRun, openLocationSettings } from "@/lib/runLocationPermission";
import { prepareAudioForRun } from "@/lib/straivNowPlaying";
import { LocationPermissionDialog } from "@/components/LocationPermissionDialog";
import type { LocationPermissionReason } from "@/components/LocationPermissionDialog";
import { Analytics } from "@/lib/analytics";
import { useProfile } from '@/hooks/useProfile';
import { useWorkouts } from '@/hooks/useWorkouts';
import { formatDistanceToNow } from 'date-fns';
import type { GpsActivityKind } from '@/lib/gpsCardioWorkout';
import { gpsActivityKindFromNavState, isGpsCardioWorkoutType, findGpsCardioExercise } from '@/lib/gpsCardioWorkout';
import type { RunningWorkoutPlan } from '@/utils/workoutImporter';
import { formatRunningWorkoutForCoach } from '@/utils/workoutImporter';
import {
  loadArnoldRunPlans,
  CHAT_DEFER_CONTEXT_REFRESH_KEY,
  PENDING_CHAT_AUTO_SEND_KEY,
  formatArnoldRunSuggestedAt,
  type ArnoldRunPlanEntry,
} from '@/lib/runningCoachPlans';
import { buildRunningLaunchSettings, normalizeCoachPlanText } from '@/lib/runningCoach';

type ActivityType = 'outdoor' | 'treadmill';
type AudioStatsInterval = '1min' | '5min' | '10min' | '500m' | '1km' | '5km' | 'off';
type AudioVoice = 'female' | 'male';
type CoachFrequency = 'rare' | 'when_needed' | 'frequent';

interface RunningCoachGuideSettings {
  enabled: boolean;
  frequency: CoachFrequency;
  plan: string;
  source?: 'manual' | 'arnold';
  planStructured?: boolean;
}

const TIME_INTERVALS: AudioStatsInterval[] = ['1min', '5min', '10min'];
const DISTANCE_INTERVALS: AudioStatsInterval[] = ['500m', '1km', '5km'];

const formatAudioIntervalLabel = (v: AudioStatsInterval): string => {
  if (v === 'off') return 'Off';
  if (v.endsWith('min')) return v.replace('min', ' min');
  return v; // 500m, 1km, 5km
};

type WorkoutIdeaId =
  | 'intervalTraining'
  | 'easyRecovery'
  | 'tempo'
  | 'lsd'
  | 'sprint';

export const RunningStart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { profile } = useProfile();
  const { workouts } = useWorkouts();
  const { user } = useAuth();

  // Surface a clear note in the AI Run Coach setup sheet when Apple Health
  // is not connected: the live coach still works (pace + structure cues),
  // it just can't read or guide by heart-rate zones.
  const [healthKitConnected, setHealthKitConnected] = useState(false);
  useEffect(() => {
    if (!user?.id) return;
    void getHealthConnectedState(user.id)
      .then(setHealthKitConnected)
      .catch(() => setHealthKitConnected(false));
  }, [user?.id]);

  const [gpsKind, setGpsKind] = useState<GpsActivityKind>(() =>
    gpsActivityKindFromNavState((location.state as { gpsActivity?: string } | null)?.gpsActivity)
  );

  useEffect(() => {
    const raw = (location.state as { gpsActivity?: string } | null)?.gpsActivity;
    setGpsKind(gpsActivityKindFromNavState(raw));
  }, [location.state]);

  // Settings state - load from localStorage
  const [activityType, setActivityType] = useState<ActivityType>('outdoor');
  const [audioStats, setAudioStats] = useState<AudioStatsInterval>('5min');
  const [audioVoice, setAudioVoice] = useState<AudioVoice>('female');
  const [coachGuide, setCoachGuide] = useState<RunningCoachGuideSettings>({
    enabled: false,
    frequency: 'when_needed',
    plan: '',
    source: 'manual',
    planStructured: false,
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [coachGuideOpen, setCoachGuideOpen] = useState(false);
  const [structuringPlan, setStructuringPlan] = useState(false);
  const structuringPlanRef = useRef(false);

  const coachAgeYears = profile?.birth_date
    ? Math.max(10, new Date().getFullYear() - new Date(profile.birth_date).getFullYear())
    : null;

  const parsedCoachPlanPreview = useMemo(() => {
    const text = coachGuide.plan.trim();
    if (!text) return null;
    try {
      return parseRunPlan(text);
    } catch (parseErr) {
      console.warn('Failed to parse run plan preview:', parseErr);
      return null;
    }
  }, [coachGuide.plan]);

  const addStructuredPlanToGuide = useCallback(async () => {
    const raw = normalizeCoachPlanText(coachGuide.plan).trim();
    if (!raw || structuringPlanRef.current) return;

    structuringPlanRef.current = true;
    setStructuringPlan(true);
    const safety = window.setTimeout(() => {
      structuringPlanRef.current = false;
      setStructuringPlan(false);
    }, 15_000);

    const finish = () => {
      window.clearTimeout(safety);
      structuringPlanRef.current = false;
      setStructuringPlan(false);
    };

    try {
      // Already a numbered structured plan — no AI needed.
      const existing = parseRunPlan(raw);
      if (existing.hasStructure && /^\d+\.\s/m.test(raw)) {
        setCoachGuide((prev) => ({
          ...prev,
          plan: raw,
          source: 'manual',
          planStructured: true,
        }));
        toast.success('Plan recognized — enable coach when ready');
        return;
      }

      const result = await structureRunGuideForCoach(raw, { ageYears: coachAgeYears });
      if (result?.plan?.trim()) {
        setCoachGuide((prev) => ({
          ...prev,
          plan: result.plan.trim(),
          source: 'manual',
          planStructured: true,
        }));
        toast.success(
          result.source === 'ai'
            ? 'Plan structured by Arnold — review and enable coach'
            : 'Plan structured — review and enable coach',
        );
        return;
      }

      // Parser can follow the plan even if we couldn't rewrite it.
      if (existing.hasStructure) {
        setCoachGuide((prev) => ({
          ...prev,
          plan: raw,
          source: 'manual',
          planStructured: true,
        }));
        toast.success('Plan recognized — enable coach when ready');
        return;
      }

      toast.error('Could not structure this plan — try "2km zone 2, 4km zone 4" or add warmup/main/cooldown.');
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err ?? 'unknown');
      console.warn('[AddToGuide] failed:', detail, err);
      toast.error('Could not structure plan right now. Try again.');
    } finally {
      finish();
    }
  }, [coachGuide.plan, coachAgeYears]);
  const [showCoachSaved, setShowCoachSaved] = useState(false);
  const [showImportRuns, setShowImportRuns] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const coachScrollRef = useRef<HTMLDivElement>(null);
  const planTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [importedRunPlans, setImportedRunPlans] = useState<ArnoldRunPlanEntry[]>([]);
  const [showWorkouts, setShowWorkouts] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState<{
    id: WorkoutIdeaId;
    title: string;
    description: string;
  } | null>(null);
  
  // GPS state
  const [currentPosition, setCurrentPosition] = useState<[number, number] | null>(null);
  const [gpsSignal, setGpsSignal] = useState<'strong' | 'medium' | 'weak' | 'none'>('none');
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null);
  const [positionRefreshKey, setPositionRefreshKey] = useState(0);
  const [isStarting, setIsStarting] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownNumber, setCountdownNumber] = useState<number | 'go'>(3);
  const pendingNavigateRef = useRef<'outdoor' | 'treadmill' | null>(null);
  const [permissionDialog, setPermissionDialog] = useState<{
    open: boolean;
    reason: LocationPermissionReason;
  }>({ open: false, reason: "notGranted" });
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [isMapCentered, setIsMapCentered] = useState(true);
  const lastProcessedRefreshKeyRef = useRef(0);
  const permissionDialogRef = useRef(permissionDialog);
  permissionDialogRef.current = permissionDialog;

  const importedRunPreviewByIndex = useMemo(
    () =>
      importedRunPlans.map(({ plan }) => {
        try {
          return formatRunningWorkoutForCoach(plan);
        } catch {
          return plan.title || 'Run workout';
        }
      }),
    [importedRunPlans],
  );
  
  // Load saved settings on mount and log screen view
  useEffect(() => {
    // Log screen opened
    Analytics.runningScreenOpened();
    Analytics.logScreenView('RunningStart');
    
    // Prepare audio session early so music keeps playing when user taps Start
    prepareAudioForRun();

    
    const savedSettings = localStorage.getItem('runningUserSettings');
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        if (settings.activityType) setActivityType(settings.activityType);
        if (settings.audioStats) setAudioStats(settings.audioStats);
        if (settings.audioVoice) setAudioVoice(settings.audioVoice);
        if (settings.coachGuide) {
          const plan = typeof settings.coachGuide.plan === 'string' ? settings.coachGuide.plan : '';
          const source = settings.coachGuide.source === 'arnold' ? 'arnold' : 'manual';
          let planStructured = source === 'arnold';
          if (!planStructured && plan.trim()) {
            try {
              planStructured = parseRunPlan(plan).hasStructure && /^\d+\.\s/m.test(plan);
            } catch {
              planStructured = false;
            }
          }
          setCoachGuide({
            enabled: false,
            frequency: ['rare', 'when_needed', 'frequent'].includes(settings.coachGuide.frequency)
              ? settings.coachGuide.frequency
              : 'when_needed',
            plan,
            source,
            planStructured,
          });
        }
      } catch (e) {
        console.warn('Failed to load saved settings:', e);
      }
    }

    try {
      setImportedRunPlans(loadArnoldRunPlans());
    } catch {}

    // Clear stale coach-enabled flag from a previous guided run so the next Start is normal.
    try {
      const raw = localStorage.getItem('runningSettings');
      if (raw) {
        const parsed = JSON.parse(raw) as { coachGuide?: { enabled?: boolean } };
        if (parsed?.coachGuide?.enabled) {
          parsed.coachGuide.enabled = false;
          localStorage.setItem('runningSettings', JSON.stringify(parsed));
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    const scrollPlanIntoView = () => {
      planTextareaRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      if (coachScrollRef.current) {
        coachScrollRef.current.scrollTop = coachScrollRef.current.scrollHeight;
      }
    };

    if (Capacitor.isNativePlatform()) {
      const isIOS = Capacitor.getPlatform() === 'ios';
      const showEvent = isIOS ? 'keyboardWillShow' : 'keyboardDidShow';
      const hideEvent = isIOS ? 'keyboardWillHide' : 'keyboardDidHide';
      let showSub: { remove: () => Promise<void> } | null = null;
      let hideSub: { remove: () => Promise<void> } | null = null;
      Keyboard.addListener(showEvent, (info) => {
        setKeyboardHeight(info.keyboardHeight || 0);
        setTimeout(scrollPlanIntoView, 120);
        setTimeout(scrollPlanIntoView, 320);
      })
        .then((sub) => {
          showSub = sub;
        })
        .catch(() => {});
      Keyboard.addListener(hideEvent, () => {
        setKeyboardHeight(0);
      })
        .then((sub) => {
          hideSub = sub;
        })
        .catch(() => {});
      return () => {
        showSub?.remove();
        hideSub?.remove();
      };
    }

    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      const kb = Math.max(0, window.innerHeight - vv.height);
      setKeyboardHeight(kb > 80 ? kb : 0);
      if (kb > 80) setTimeout(scrollPlanIntoView, 100);
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  // Warm up cloud TTS once the coach is enabled so countdown audio clips load
  // in time. Normal runs use on-device voice so no warm-up needed.
  useEffect(() => {
    if (!coachGuide.enabled) return;
    void prefetchCoachAudio('Ready', audioVoice).catch(() => {});
  }, [coachGuide.enabled, audioVoice]);

  useEffect(() => {
    const state = location.state as { openCoachGuide?: boolean; coachGuidePlan?: string; gpsActivity?: string } | null;
    if (!state?.openCoachGuide && !state?.coachGuidePlan) return;
    if (state.coachGuidePlan) {
      const planText = normalizeCoachPlanText(state.coachGuidePlan);
      setCoachGuide((prev) => ({
        ...prev,
        enabled: true,
        plan: planText || prev.plan,
        source: 'arnold',
        planStructured: true,
      }));
    }
    setCoachGuideOpen(true);
    navigate(location.pathname, { replace: true, state: state?.gpsActivity ? { gpsActivity: state.gpsActivity } : null });
  }, [location.pathname, location.state, navigate]);
  
  // Save settings when they change (coach enabled flag is session-only — not persisted).
  useEffect(() => {
    const settings = {
      activityType,
      audioStats,
      audioVoice,
      coachGuide: {
        frequency: coachGuide.frequency,
        plan: coachGuide.plan,
        source: coachGuide.source,
      },
    };
    localStorage.setItem('runningUserSettings', JSON.stringify(settings));
  }, [activityType, audioStats, audioVoice, coachGuide.frequency, coachGuide.plan, coachGuide.source]);

  const planReadyForCoach = coachGuide.planStructured === true && coachGuide.plan.trim().length > 0;
  const needsAddToGuide = coachGuide.plan.trim().length > 0 && !planReadyForCoach;

  const effectiveGpsKind: GpsActivityKind =
    activityType === 'treadmill' ? 'run' : gpsKind;

  const lastGpsSummary = useMemo(() => {
    const sorted = [...workouts]
      .filter((w) => w.completed_at)
      .sort(
        (a, b) =>
          new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime()
      );
    for (const w of sorted.slice(0, 40)) {
      if (!isGpsCardioWorkoutType(w.workout_type)) continue;
      const ex = findGpsCardioExercise(w.exercises);
      if (!ex?.distance_km || !w.completed_at) continue;
      return {
        km: ex.distance_km,
        completedAt: w.completed_at,
        pace: (() => {
          try {
            const n = ex.notes ? JSON.parse(ex.notes) : null;
            return n?.pace as string | undefined;
          } catch {
            return undefined;
          }
        })(),
      };
    }
    return null;
  }, [workouts]);

  const weeklyOutdoorKm = useMemo(() => {
    const since = new Date();
    since.setDate(since.getDate() - 7);
    let km = 0;
    for (const w of workouts) {
      if (!w.completed_at || new Date(w.completed_at) < since) continue;
      if (!isGpsCardioWorkoutType(w.workout_type)) continue;
      const ex = findGpsCardioExercise(w.exercises);
      if (ex?.distance_km) km += ex.distance_km;
    }
    return Math.round(km * 10) / 10;
  }, [workouts]);

  const buildFallbackRunPlanText = (workout: { title: string; description: string }) => {
    const primaryIdea = selectedWorkout ?? runningWorkouts[0];
    const historyLine = lastGpsSummary
      ? `Last run: ${lastGpsSummary.km.toFixed(1)} km at ${lastGpsSummary.pace || 'unknown pace'}.`
      : 'No previous outdoor run found yet.';
    return `${workout.title || primaryIdea.title}: ${workout.description || primaryIdea.description}\n${historyLine}`;
  };

  const importArnoldRunSuggestion = () => {
    setImportedRunPlans(loadArnoldRunPlans());
    setShowImportRuns(true);
  };

  const askArnoldForRunGuide = () => {
    const coachName = profile?.ai_coach_name || 'Arnold';
    const prompt =
      `${coachName}, suggest a run workout I can follow with the AI running guide. ` +
      'Include a warmup, main set, and cooldown with specific distances or durations and target zones or pace.';
    try {
      sessionStorage.setItem(PENDING_CHAT_AUTO_SEND_KEY, prompt);
      sessionStorage.setItem(CHAT_DEFER_CONTEXT_REFRESH_KEY, '1');
    } catch {
      /* ignore quota errors */
    }
    navigate('/chat', { state: { message: prompt } });
  };

  const chooseFallbackRunIdea = (workout: { title: string; description: string }) => {
    setCoachGuide({
      enabled: true,
      frequency: coachGuide.frequency,
      plan: buildFallbackRunPlanText(workout),
      source: 'arnold',
      planStructured: true,
    });
    setShowImportRuns(false);
  };

  const chooseImportedRunPlan = (plan: RunningWorkoutPlan) => {
    try {
      setCoachGuide((prev) => ({
        ...prev,
        enabled: true,
        plan: formatRunningWorkoutForCoach(plan),
        source: 'arnold',
        planStructured: true,
      }));
      setShowImportRuns(false);
    } catch (err) {
      console.error('Failed to import Arnold run plan:', err);
      toast.error('Could not load this run workout.');
    }
  };

  // Request location permission and get position on mount (and when returning from Settings)
  useEffect(() => {
    const isNative = Capacitor.isNativePlatform();
    if (!isNative) return;
    
    let isMounted = true;
    let watchId: string | null = null;
    const isRefresh = positionRefreshKey > 0;
    
    const getLocation = async () => {
      try {
        const currentPermission = await Geolocation.checkPermissions();
        let locationGranted = currentPermission.location === 'granted';
        if (!locationGranted && !isRefresh) {
          Analytics.runningGpsPermissionRequested();
          const requestedPermission = await Geolocation.requestPermissions();
          locationGranted = requestedPermission.location === 'granted';
          if (locationGranted) Analytics.runningGpsPermissionGranted();
        }
        if (!locationGranted) {
          if (!isRefresh) Analytics.runningGpsPermissionDenied();
          if (isMounted) {
            setPermissionDialog({ open: true, reason: 'denied' });
            setGpsSignal('none');
          }
          return;
        }
        
        // Try to get a quick low-accuracy position first for instant feedback
        try {
          const quickPosition = await Geolocation.getCurrentPosition({
            enableHighAccuracy: false, // Faster, less accurate
            timeout: 3000, // 3 second timeout for quick fix
            maximumAge: 30000, // Accept cached position up to 30 seconds old
          });
          
          if (isMounted && quickPosition) {
            setCurrentPosition([quickPosition.coords.latitude, quickPosition.coords.longitude]);
            setGpsAccuracy(quickPosition.coords.accuracy);
            setGpsSignal('weak'); // Mark as weak since it's low accuracy
          }
        } catch (quickError) {
          console.log('Quick position not available, waiting for high accuracy...');
        }
        
        // Now get high-accuracy position
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000, // Reduced from 15s to 10s
          maximumAge: 5000, // Accept position up to 5 seconds old
        });
        
        if (isMounted && position) {
          setCurrentPosition([position.coords.latitude, position.coords.longitude]);
          setGpsAccuracy(position.coords.accuracy);
          
          // Set signal strength based on accuracy
          let signalStrength: 'strong' | 'medium' | 'weak' | 'none' = 'none';
          if (position.coords.accuracy <= 5) {
            signalStrength = 'strong';
          } else if (position.coords.accuracy <= 15) {
            signalStrength = 'medium';
          } else if (position.coords.accuracy <= 30) {
            signalStrength = 'weak';
          }
          setGpsSignal(signalStrength);
          
          // Log GPS signal acquired
          Analytics.runningGpsSignalAcquired(position.coords.accuracy, signalStrength);
        }
        
        // Continue watching for better accuracy
        watchId = await Geolocation.watchPosition(
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          },
          (pos, err) => {
            if (err || !pos || !isMounted) return;
            
            setCurrentPosition([pos.coords.latitude, pos.coords.longitude]);
            setGpsAccuracy(pos.coords.accuracy);
            
            if (pos.coords.accuracy <= 5) {
              setGpsSignal('strong');
            } else if (pos.coords.accuracy <= 15) {
              setGpsSignal('medium');
            } else if (pos.coords.accuracy <= 30) {
              setGpsSignal('weak');
            } else {
              setGpsSignal('none');
            }
          }
        );
      } catch (error) {
        console.warn('Failed to get location:', error);
        if (isMounted) {
          setGpsSignal('none');
        }
      }
    };
    
    getLocation();
    
    return () => {
      isMounted = false;
      if (watchId) {
        Geolocation.clearWatch({ id: watchId }).catch(console.warn);
      }
    };
  }, [positionRefreshKey]);
  
  // Arnold's running workouts data
  const runningWorkouts = useMemo(
    () =>
      (
        [
          'intervalTraining',
          'easyRecovery',
          'tempo',
          'lsd',
          'sprint',
        ] as const
      ).map((id) => ({
        id,
        title: t(`running.start.workouts.${id}.title`),
        description: t(`running.start.workouts.${id}.description`),
      })),
    [t]
  );
  
  // Unlock audio session in user gesture context (required for iOS - TTS won't work otherwise)
  const unlockAudioSession = () => {
    try {
      if (typeof window === 'undefined') return;
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        ctx.resume?.(); // iOS starts suspended
        const buf = ctx.createBuffer(1, 128, ctx.sampleRate);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(ctx.destination);
        src.start(0);
      }
    } catch {}
  };

  // Re-check permission when app returns from Settings - close if Always, or update dialog to current reason
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") return;
    const sub = App.addListener("appStateChange", async ({ isActive }) => {
      if (!isActive || !permissionDialogRef.current.open) return;
      // Delay so iOS has updated CLLocationManager.authorizationStatus after returning from Settings
      await new Promise((r) => setTimeout(r, 800));
      const result = await ensureLocationForOutdoorRun();
      if (result.ok) {
        setPermissionDialog({ open: false, reason: "notGranted" });
        setPositionRefreshKey((k) => k + 1);
      } else {
        const failResult = result as { ok: false; reason: LocationPermissionReason };
        setPermissionDialog({ open: true, reason: failResult.reason });
        if (failResult.reason === "whenInUse") {
          setPositionRefreshKey((k) => k + 1);
        }
      }
    });
    return () => {
      sub.then((l) => l.remove());
    };
  }, []);

  /** Coach only when explicitly enabled this session + plan exists. */
  const buildRunLaunchSettings = useCallback(
    (startTime: string) =>
      buildRunningLaunchSettings({
        activityType,
        audioStats,
        audioVoice,
        coachGuide,
        startTime,
        gpsActivity: effectiveGpsKind,
      }),
    [activityType, audioStats, audioVoice, coachGuide, effectiveGpsKind],
  );
  const buildRunLaunchSettingsRef = useRef(buildRunLaunchSettings);
  buildRunLaunchSettingsRef.current = buildRunLaunchSettings;

  // Countdown timer: 3, 2, 1, Go - visual + audio (always plays unless audioStats off)
  useEffect(() => {
    if (!showCountdown || !pendingNavigateRef.current) return;
    let cancelled = false;
    const target = pendingNavigateRef.current;

    const runCountdown = async () => {
      const steps: { n: number | 'go'; text: string }[] = [
        { n: 3, text: 'Three' },
        { n: 2, text: 'Two' },
        { n: 1, text: 'One' },
        { n: 'go', text: 'Go!' },
      ];

      const useCountdownAudio = audioStats !== 'off';
      stopCoachVoice();

      // Countdown always uses on-device voice (instant, consistent robot cue).
      // OpenAI / natural voice is reserved for in-run AI coach guidance only.
      if (cancelled) return;

      for (let i = 0; i < steps.length; i++) {
        if (cancelled) return;
        const { n, text } = steps[i];
        setCountdownNumber(n);

        if (useCountdownAudio) {
          await speakNativeNow(text, audioVoice);
        } else {
          await new Promise((r) => setTimeout(r, 650));
        }

        if (cancelled) return;
        const gap = n === 'go' ? 180 : 300;
        await new Promise((r) => setTimeout(r, gap));
      }

      pendingNavigateRef.current = null;
      setShowCountdown(false);
      setCountdownNumber(3);

      const startTime = new Date().toISOString();
      const settings = buildRunLaunchSettingsRef.current(startTime);
      localStorage.setItem('runningSettings', JSON.stringify(settings));

      if (target === 'outdoor') {
        navigate('/running-active');
      } else {
        navigate('/running-workout');
      }
    };

    void runCountdown();
    return () => {
      cancelled = true;
    };
  }, [showCountdown, navigate, audioStats, audioVoice]);

  const handleStart = async () => {
    if (isStarting) return; // Guard against rapid double-taps while async perm flow runs
    setIsStarting(true);
    try {
      prepareAudioForRun(); // Set ambient + mixWithOthers before any audio - avoids music pause
      // Skip unlockAudioSession - Web Audio activation pauses music; TTS works without it
      const startTime = new Date().toISOString();
      const settings = buildRunLaunchSettings(startTime);
      localStorage.setItem('runningSettings', JSON.stringify(settings));
      Analytics.runningWorkoutStarted(activityType === 'outdoor', {
        activity_type: activityType,
        audio_interval: audioStats,
        audio_voice: audioVoice,
        gps_signal: gpsSignal,
        gps_accuracy: gpsAccuracy
      });
      if (activityType === 'outdoor') {
        // Check location BEFORE countdown - don't start countdown if not ready
        const result = await ensureLocationForOutdoorRun();
        if (!result.ok) {
          const failResult = result as { ok: false; reason: import('@/lib/runLocationPermission').LocationPermissionReason };
          setPermissionDialog({ open: true, reason: failResult.reason });
          return;
        }
        // Go straight to countdown - the legacy "Important Notice" warning is no longer needed
        // (TTS now uses ambient + mixWithOthers so music doesn't get paused).
        pendingNavigateRef.current = 'outdoor';
        setShowCountdown(true);
      } else {
        // Indoor: no countdown, go straight to treadmill workout
        navigate('/running-workout');
      }
    } finally {
      setIsStarting(false);
    }
  };

  const getSignalIcon = () => {
    if (gpsSignal === 'strong') return <SignalHigh className="h-5 w-5 text-primary" />;
    if (gpsSignal === 'medium') return <SignalMedium className="h-5 w-5 text-amber-500" />;
    if (gpsSignal === 'weak') return <SignalLow className="h-5 w-5 text-orange-500" />;
    return <Signal className="h-5 w-5 text-destructive" />;
  };

  const getSignalBars = () => {
    const bars = gpsSignal === 'strong' ? 4 : gpsSignal === 'medium' ? 3 : gpsSignal === 'weak' ? 2 : 1;
    return (
      <div className="flex items-end gap-0.5 h-4">
        {[1, 2, 3, 4].map((bar) => (
          <div
            key={bar}
            className={`w-1 rounded-sm transition-all ${
              bar <= bars 
                ? 'bg-primary' 
                : 'bg-muted-foreground/30'
            }`}
            style={{ height: `${bar * 25}%` }}
          />
        ))}
      </div>
    );
  };

  // Set/update map center when position is available (including once per refresh from Settings)
  useEffect(() => {
    if (!currentPosition) return;
    if (!mapCenter) {
      setMapCenter(currentPosition);
    } else if (positionRefreshKey > lastProcessedRefreshKeyRef.current) {
      setMapCenter([...currentPosition]);
      lastProcessedRefreshKeyRef.current = positionRefreshKey;
    }
  }, [currentPosition, mapCenter, positionRefreshKey]);
  
  // Re-center map to current position
  const handleRecenterMap = () => {
    if (currentPosition) {
      setMapCenter([...currentPosition]);
    }
    setIsMapCentered(true); // Show dot, hide recenter button
    Analytics.runningMapRecentered();
  };
  
  // Handle map interaction (user scrolled/panned away)
  const handleMapInteraction = () => {
    setIsMapCentered(false); // Hide dot, show recenter button
    Analytics.runningMapScrolled();
  };
  
  // Use mapCenter for map display, fallback to currentPosition or default
  const mapLat = mapCenter ? mapCenter[0] : (currentPosition ? currentPosition[0] : 59.3293);
  const mapLng = mapCenter ? mapCenter[1] : (currentPosition ? currentPosition[1] : 18.0686);
  
  return (
    <div className="fixed inset-0 bg-background flex flex-col">
      {/* Map Background - Apple Maps with user location marker */}
      <div className="absolute inset-0 z-0">
        <PlatformMap 
          key={positionRefreshKey > 0 ? `map-refresh-${positionRefreshKey}` : 'map-initial'}
          center={mapCenter || currentPosition}
          userLocation={currentPosition}
          showUserMarker={true}
          className="w-full h-full"
          darkMode={true}
          autoFollow={isMapCentered}
          onUserScroll={handleMapInteraction}
        />
        
        {/* Gradient overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/30 pointer-events-none z-20" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-background/60 to-transparent pointer-events-none z-20" />
      </div>
      
      {/* Settings Sheet */}
      <Sheet open={settingsOpen} onOpenChange={(open) => {
        setSettingsOpen(open);
        if (open) Analytics.runningSettingsOpened();
      }}>
          <SheetContent side="bottom" className="rounded-t-3xl pb-8">
            <SheetHeader className="pb-4">
              <SheetTitle className="text-center">{t('running.start.sheetTitle')}</SheetTitle>
            </SheetHeader>
            
            <div className="space-y-1">
              {/* Activity Type */}
              <div className="flex items-center justify-between py-4 border-b border-border/50">
                <div className="flex items-center gap-3">
                  <Footprints className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('running.start.activity.label')}</p>
                    <p className="font-semibold">{t('running.start.activity.running')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={activityType === 'outdoor' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setActivityType('outdoor');
                      Analytics.runningActivityTypeChanged('outdoor');
                    }}
                    className={`text-xs ${activityType === 'outdoor' ? 'bg-primary text-primary-foreground' : ''}`}
                  >
                    {t('running.start.activity.outdoor')}
                  </Button>
                  <Button
                    variant={activityType === 'treadmill' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setActivityType('treadmill');
                      Analytics.runningActivityTypeChanged('treadmill');
                    }}
                    className={`text-xs ${activityType === 'treadmill' ? 'bg-primary text-primary-foreground' : ''}`}
                  >
                    {t('running.start.activity.treadmill')}
                  </Button>
                </div>
              </div>
              
              {/* Show Workouts */}
              <div 
                className="flex items-center justify-between py-4 border-b border-border/50 cursor-pointer"
                onClick={() => {
                  setShowWorkouts(true);
                  Analytics.runningWorkoutsListOpened();
                }}
              >
                <div className="flex items-center gap-3">
                  <ListChecks className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('running.start.workouts.rowLabel')}</p>
                    <p className="font-semibold">{t('running.start.workouts.rowSubtitle')}</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
              
              {/* Audio Stats Interval - time OR distance (mutually exclusive) */}
              <div className="flex items-center justify-between py-4 border-b border-border/50">
                <div className="flex items-center gap-3">
                  {audioStats === 'off' ? (
                    <VolumeX className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <Volume2 className="h-5 w-5 text-primary" />
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground">{t('running.start.audio.intervalLabel')}</p>
                    <p className="font-semibold">{audioStats === 'off' ? t('running.audioInterval.off') : formatAudioIntervalLabel(audioStats)}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground shrink-0 w-14">{t('running.start.audio.byTime')}</span>
                    {TIME_INTERVALS.map((interval) => (
                      <Button
                        key={interval}
                        variant={audioStats === interval ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => {
                          setAudioStats(interval);
                          Analytics.runningAudioIntervalChanged(interval);
                        }}
                        className={`text-xs px-2 ${audioStats === interval ? 'bg-primary text-primary-foreground' : ''}`}
                      >
                        {interval}
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground shrink-0 w-14">{t('running.start.audio.byDistance')}</span>
                    {DISTANCE_INTERVALS.map((interval) => (
                      <Button
                        key={interval}
                        variant={audioStats === interval ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => {
                          setAudioStats(interval);
                          Analytics.runningAudioIntervalChanged(interval);
                        }}
                        className={`text-xs px-2 ${audioStats === interval ? 'bg-primary text-primary-foreground' : ''}`}
                      >
                        {interval}
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant={audioStats === 'off' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        setAudioStats('off');
                        Analytics.runningAudioIntervalChanged('off');
                      }}
                      className={`text-xs px-2 ${audioStats === 'off' ? 'bg-primary text-primary-foreground' : ''}`}
                    >
                      {t('running.audioInterval.off')}
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Audio Voice */}
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <Volume2 className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('running.start.audio.voiceLabel')}</p>
                    <p className="font-semibold">{audioVoice === 'female' ? t('running.start.audio.voiceFemale') : t('running.start.audio.voiceMale')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {(['female', 'male'] as AudioVoice[]).map((voice) => (
                    <Button
                      key={voice}
                      variant={audioVoice === voice ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        setAudioVoice(voice);
                        Analytics.runningAudioVoiceChanged(voice);
                      }}
                      className={`text-xs px-2 ${audioVoice === voice ? 'bg-primary text-primary-foreground' : ''}`}
                    >
                      {voice === 'female' ? t('running.start.audio.voiceFemale') : t('running.start.audio.voiceMale')}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Arnold Coach Guide Sheet */}
        <Sheet open={coachGuideOpen} onOpenChange={setCoachGuideOpen}>
          <SheetContent
            side="bottom"
            className="h-[85vh] max-h-[85vh] rounded-t-3xl [&>button]:hidden flex flex-col"
            style={{
              transform: keyboardHeight > 0 ? `translateY(-${keyboardHeight}px)` : undefined,
              transition: keyboardHeight > 0 ? 'transform 0.22s ease-out' : undefined,
            }}
          >
            <SheetHeader className="pb-4 relative shrink-0">
              <button
                onClick={() => setCoachGuideOpen(false)}
                className="absolute right-0 top-0 p-2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
              <SheetTitle className="text-center">Arnold run coach</SheetTitle>
              <p className="text-center text-xs text-muted-foreground px-5">
                Live Straiv guidance for your target, intervals, and pacing.
              </p>
            </SheetHeader>

            <div ref={coachScrollRef} className="flex-1 overflow-y-auto min-h-0 space-y-5 pb-4">
              <div className="rounded-2xl border border-primary/25 bg-primary/5 p-4">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-full bg-gradient-primary text-black flex items-center justify-center shadow-glow">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold">Personal trainer audio</p>
                    <p className="text-xs text-muted-foreground">
                      Choose how often Arnold should coach you during this run.
                    </p>
                  </div>
                </div>
              </div>

              {/* Apple Health connection status: be explicit so the user knows
                  whether HR-zone coaching will be active or not. */}
              <div
                className={`rounded-2xl border p-3 flex items-start gap-3 ${
                  healthKitConnected
                    ? 'border-emerald-500/30 bg-emerald-500/5'
                    : 'border-amber-500/30 bg-amber-500/5'
                }`}
              >
                <div className={`h-9 w-9 rounded-full flex items-center justify-center ${
                  healthKitConnected ? 'bg-emerald-500/15 text-emerald-500' : 'bg-amber-500/15 text-amber-500'
                }`}>
                  <HeartPulse className="h-5 w-5" />
                </div>
                <div className="text-xs leading-snug">
                  {healthKitConnected ? (
                    <>
                      <p className="font-semibold mb-0.5">Apple Health connected</p>
                      <p className="text-muted-foreground">
                        Arnold will track your live heart rate and coach you by zone whenever the plan specifies one.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold mb-0.5">Apple Health not connected</p>
                      <p className="text-muted-foreground">
                        Arnold can still guide pace and follow the structure of the plan, but he can't read your heart rate. Connect Apple Health in Settings to unlock zone-based coaching.
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Guidance frequency</p>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    ['rare', 'Rare', 'Max every 5 min'],
                    ['when_needed', 'When needed', 'About 90 sec'],
                    ['frequent', 'Frequent', 'About 30 sec'],
                  ] as const).map(([value, label, hint]) => (
                    <Button
                      key={value}
                      type="button"
                      variant={coachGuide.frequency === value ? 'default' : 'outline'}
                      onClick={() => setCoachGuide((prev) => ({ ...prev, frequency: value }))}
                      className={`h-auto min-h-[4.75rem] flex-col gap-1 px-2 text-center ${
                        coachGuide.frequency === value ? 'bg-gradient-primary text-black border-transparent' : ''
                      }`}
                    >
                      <span className="text-xs font-semibold">{label}</span>
                      <span className="text-[10px] opacity-75">{hint}</span>
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Run plan</p>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={askArnoldForRunGuide}
                      className="h-8 text-xs text-primary"
                    >
                      <MessageCircle className="h-3.5 w-3.5 mr-1" />
                      Ask {profile?.ai_coach_name || 'Arnold'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={importArnoldRunSuggestion}
                      className="h-8 text-xs text-primary"
                    >
                      Import from Arnold
                    </Button>
                  </div>
                </div>
                <Textarea
                  ref={planTextareaRef}
                  value={coachGuide.plan}
                  onChange={(event) =>
                    setCoachGuide((prev) => ({
                      ...prev,
                      plan: event.target.value,
                      source: 'manual',
                      planStructured: false,
                      enabled: false,
                    }))
                  }
                  placeholder={
                    'Example: 5 min warmup at Z2. 6x800m at threshold (Z4) with 90 sec easy jog. 10 min cooldown at Z1.'
                  }
                  className="min-h-[140px] bg-background/80"
                  style={{ scrollMarginBottom: keyboardHeight > 0 ? keyboardHeight + 24 : undefined }}
                  onFocus={() => {
                    setTimeout(() => {
                      planTextareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      if (coachScrollRef.current) {
                        coachScrollRef.current.scrollTop = coachScrollRef.current.scrollHeight;
                      }
                    }, 150);
                  }}
                />
                {needsAddToGuide && (
                  <p className="mt-1.5 text-[11px] text-primary">
                    Tap Add to guide below so Arnold understands your run structure before enabling the coach.
                  </p>
                )}

                {parsedCoachPlanPreview && (() => {
                  const parsed = parsedCoachPlanPreview;
                  if (!parsed.hasStructure) {
                    return (
                      <div className="mt-2 rounded-lg border border-border/40 bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground flex items-start gap-2">
                        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                          Free-form plan detected. Arnold will guide pacing and effort, but won't time specific phases. Add steps like "5 min warmup", "6x800m at Z4", or "10 min cooldown" for structured coaching.
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div className="mt-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-[11px]">
                      <p className="font-semibold mb-1 text-primary">Arnold will follow {parsed.phases.length} phase{parsed.phases.length !== 1 ? 's' : ''}:</p>
                      <ol className="space-y-0.5 text-muted-foreground list-decimal pl-4">
                        {parsed.phases.slice(0, 8).map((p) => (
                          <li key={p.index}>
                            <span className="text-foreground">{p.label}</span>
                            {p.durationSeconds ? ` · ${Math.round(p.durationSeconds / 60)}m${(p.durationSeconds % 60) ? ` ${p.durationSeconds % 60}s` : ''}` : ''}
                            {p.distanceKm ? ` · ${p.distanceKm}km` : ''}
                            {p.paceSecondsPerKm ? ` · ${Math.floor(p.paceSecondsPerKm / 60)}:${String(Math.round(p.paceSecondsPerKm % 60)).padStart(2, '0')}/km` : ''}
                            {p.targetZone ? ` · ${p.targetZone.toUpperCase()}` : ''}
                          </li>
                        ))}
                        {parsed.phases.length > 8 && (
                          <li className="opacity-70">+ {parsed.phases.length - 8} more</li>
                        )}
                      </ol>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="shrink-0 pt-3 border-t border-border/40 space-y-2">
              {coachGuide.plan.trim().length > 0 && (
                <Button
                  type="button"
                  size="sm"
                  variant={needsAddToGuide ? 'default' : 'secondary'}
                  disabled={structuringPlan}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void addStructuredPlanToGuide();
                  }}
                  className={`w-full min-h-11 text-xs touch-manipulation ${
                    needsAddToGuide
                      ? 'bg-gradient-primary text-black ring-2 ring-primary/60 shadow-glow'
                      : ''
                  }`}
                >
                  {structuringPlan ? 'Structuring…' : needsAddToGuide ? 'Add to guide (required)' : 'Add to guide'}
                </Button>
              )}
              <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className={`flex-1 ${
                  !coachGuide.enabled
                    ? 'bg-gradient-primary text-black border-transparent hover:opacity-90'
                    : ''
                }`}
                onClick={() => setCoachGuide((prev) => ({ ...prev, enabled: false }))}
              >
                Off
              </Button>
              <Button
                type="button"
                variant={coachGuide.enabled ? 'default' : 'outline'}
                disabled={!coachGuide.enabled && !planReadyForCoach}
                className={`flex-[2] ${
                  coachGuide.enabled
                    ? 'bg-gradient-primary text-black border-transparent hover:opacity-90'
                    : planReadyForCoach
                      ? 'border-primary text-primary'
                      : 'opacity-50'
                }`}
                onClick={() => {
                  if (coachGuide.enabled) {
                    setCoachGuideOpen(false);
                    return;
                  }
                  if (!planReadyForCoach) return;
                  setCoachGuide((prev) => ({ ...prev, enabled: true }));
                  setCoachGuideOpen(false);
                  setShowCoachSaved(true);
                }}
              >
                {coachGuide.enabled ? 'Coach enabled' : 'Enable coach'}
              </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        <AlertDialog open={showCoachSaved} onOpenChange={setShowCoachSaved}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Coach ready</AlertDialogTitle>
              <AlertDialogDescription>
                {(profile?.ai_coach_name || 'Arnold')} will guide you when starting run.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction className="bg-gradient-primary text-black">
                OK
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={showImportRuns} onOpenChange={setShowImportRuns}>
          <DialogContent className="max-w-sm rounded-3xl">
            <DialogHeader>
              <DialogTitle>Choose Arnold run</DialogTitle>
              <DialogDescription>
                Pick one of Arnold's suggested runs to load into AI Run Guide.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
              {importedRunPlans.length > 0 ? (
                importedRunPlans.map((entry, index) => {
                  const { plan, suggestedAt } = entry;
                  const suggestedLabel = formatArnoldRunSuggestedAt(suggestedAt);
                  return (
                  <button
                    key={`${plan.title || 'run'}-${suggestedAt || index}`}
                    type="button"
                    onClick={() => chooseImportedRunPlan(plan)}
                    className="w-full text-left rounded-xl border border-primary/20 bg-primary/5 px-3 py-3 active:scale-[0.99]"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold">{plan.title || `Run workout ${index + 1}`}</p>
                      {suggestedLabel ? (
                        <span className="text-[10px] text-muted-foreground shrink-0">{suggestedLabel}</span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-3">
                      {importedRunPreviewByIndex[index]}
                    </p>
                  </button>
                  );
                })
              ) : (
                runningWorkouts.map((workout) => (
                  <button
                    key={workout.id}
                    type="button"
                    onClick={() => chooseFallbackRunIdea(workout)}
                    className="w-full text-left rounded-xl border border-border/50 bg-muted/30 px-3 py-3 active:scale-[0.99]"
                  >
                    <p className="text-sm font-semibold">{workout.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-3">{workout.description}</p>
                  </button>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Show Workouts Sheet */}
        <Sheet open={showWorkouts} onOpenChange={setShowWorkouts}>
          <SheetContent side="bottom" className="h-[80vh] rounded-t-3xl [&>button]:hidden">
            <SheetHeader className="pb-4 relative">
              <button
                onClick={() => setShowWorkouts(false)}
                className="absolute right-0 top-0 p-2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
              <SheetTitle className="text-xl text-center">{t('running.start.workouts.sheetTitle')}</SheetTitle>
              <p className="text-center text-xs text-muted-foreground mt-1 px-2">
                {t('running.start.workouts.sheetSubtitle')}
              </p>
            </SheetHeader>

            <div className="rounded-xl border border-border/40 bg-muted/20 p-3 mb-3 text-xs text-muted-foreground leading-relaxed">
              <div className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div>
                  {lastGpsSummary ? (
                    <p>
                      {t('running.start.workouts.personalLastSession', {
                        km: lastGpsSummary.km.toFixed(1),
                        when: formatDistanceToNow(new Date(lastGpsSummary.completedAt), {
                          addSuffix: true,
                        }),
                        pace: lastGpsSummary.pace || '—',
                      })}
                    </p>
                  ) : (
                    <p>{t('running.start.workouts.personalNoSession')}</p>
                  )}
                  {weeklyOutdoorKm > 0 ? (
                    <p className="mt-1.5 text-foreground/90">
                      {t('running.start.workouts.personalWeeklyKm', { km: weeklyOutdoorKm })}
                    </p>
                  ) : null}
                  {profile?.weight_kg ? (
                    <p className="mt-1.5">
                      {t('running.start.workouts.personalWeightHint', {
                        kg: Math.round(profile.weight_kg),
                      })}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
            
            <div className="space-y-3 overflow-y-auto max-h-[calc(80vh-100px)]">
              {runningWorkouts.map((workout) => (
                <div 
                  key={workout.id}
                  className="p-4 bg-muted/30 rounded-xl border border-border/30 cursor-pointer hover:bg-muted/50 transition-colors active:scale-[0.98]"
                  onClick={() => {
                    setSelectedWorkout(workout);
                    Analytics.runningWorkoutSelected(workout.title);
                  }}
                >
                  <h3 className="font-semibold text-base">{workout.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-3">
                    {workout.description}
                  </p>
                </div>
              ))}
              
              <p className="text-xs text-center text-muted-foreground py-4">
                {t('running.start.workouts.footerHint')}
              </p>
            </div>
          </SheetContent>
        </Sheet>
        
        {/* Selected Workout Detail Sheet */}
        <Sheet open={!!selectedWorkout} onOpenChange={(open) => !open && setSelectedWorkout(null)}>
          <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl [&>button]:hidden flex flex-col">
            <SheetHeader className="pb-3 shrink-0">
              <SheetTitle className="text-xl text-center">{selectedWorkout?.title}</SheetTitle>
            </SheetHeader>
            
            <div className="flex-1 overflow-y-auto min-h-0 space-y-4 pb-2">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {selectedWorkout?.description}
              </p>
              {selectedWorkout && (
                <p className="text-sm text-foreground whitespace-pre-line leading-relaxed">
                  {t(`running.start.workouts.${selectedWorkout.id}.detail`)}
                </p>
              )}
              <div className="rounded-xl border border-primary/25 bg-primary/5 p-3 text-xs text-foreground/90 leading-relaxed">
                <p className="font-medium text-primary mb-1 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" />
                  {t('running.start.workouts.forYouHeading')}
                </p>
                {lastGpsSummary ? (
                  <p>
                    {t('running.start.workouts.forYouWithHistory', {
                      idea: selectedWorkout?.title || '',
                      km: lastGpsSummary.km.toFixed(1),
                      pace: lastGpsSummary.pace || '—',
                    })}
                  </p>
                ) : (
                  <p>
                    {t('running.start.workouts.forYouNoHistory', {
                      idea: selectedWorkout?.title || '',
                    })}
                  </p>
                )}
              </div>
            </div>
            
            <div className="pt-3 shrink-0 flex flex-col gap-2 border-t border-border/40">
              <Button
                onClick={() => {
                  if (!selectedWorkout) return;
                  const coach = profile?.ai_coach_name || t('running.start.coachFallbackName');
                  const personal = lastGpsSummary
                    ? t('running.start.workouts.aiPrefillLastSession', {
                        km: lastGpsSummary.km.toFixed(1),
                        when: formatDistanceToNow(new Date(lastGpsSummary.completedAt), {
                          addSuffix: true,
                        }),
                        pace: lastGpsSummary.pace || '—',
                      })
                    : t('running.start.workouts.aiPrefillNoSession');
                  const weightLine = profile?.weight_kg
                    ? `\n${t('running.start.workouts.aiPrefillWeight', { kg: Math.round(profile.weight_kg) })}`
                    : '';
                  const weeklyLine =
                    weeklyOutdoorKm > 0
                      ? `\n${t('running.start.workouts.aiPrefillWeeklyKm', { km: weeklyOutdoorKm })}`
                      : '';
                  const msg = t('running.start.workouts.aiChatMessage', {
                    idea: selectedWorkout.title,
                    coach,
                    personal,
                    weightLine,
                    weeklyLine,
                  });
                  setSelectedWorkout(null);
                  setShowWorkouts(false);
                  navigate('/chat', { state: { message: msg } });
                }}
                className="w-full gap-2 h-12 font-semibold bg-gradient-primary text-primary-foreground hover:opacity-90"
              >
                <MessageCircle className="h-4 w-4" />
                {t('running.start.workouts.aiCoachTipsCta', {
                  coach: profile?.ai_coach_name || t('running.start.coachFallbackName'),
                })}
              </Button>
              <Button 
                onClick={() => setSelectedWorkout(null)}
                variant="outline"
                className="w-full"
              >
                {t('common.close')}
              </Button>
            </div>
          </SheetContent>
        </Sheet>

      {/* Top UI (settings button only - no exit button) */}
      <div className="relative z-10 px-4" style={{ paddingTop: `calc(${SAFE_AREA.top} + 12px)` }}>
        {/* Logo centered */}
        <div className="flex items-center justify-center py-3">
          <img
            src={theme === "dark" ? logoDark : logoLight}
            alt={t('running.start.logoAlt')}
            className="h-8 object-contain"
          />
        </div>
        {effectiveGpsKind !== 'run' ? (
          <p className="text-center text-xs text-primary font-medium -mt-1 pb-1">
            {effectiveGpsKind === 'walk'
              ? t('running.start.gpsModeWalk')
              : t('running.start.gpsModeHike')}
          </p>
        ) : null}

        {/* Settings and coach buttons */}
        <div className="pb-2 flex flex-col items-start gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSettingsOpen(true)}
            className="h-11 w-11 rounded-full bg-background/90 backdrop-blur-md shadow-lg border border-border/30"
          >
            <Settings className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCoachGuideOpen(true)}
            className={`h-11 w-11 rounded-full backdrop-blur-md shadow-lg border border-border/30 ${
              coachGuide.enabled
                ? 'bg-gradient-primary text-black shadow-glow'
                : 'bg-background/90 text-primary'
            }`}
            aria-label="Open Arnold run coach"
          >
            <Sparkles className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1" />
      
      {/* Bottom Section */}
      <div className="relative z-10 px-4 pb-4" style={{ paddingBottom: `calc(${SAFE_AREA.bottom} + 80px)` }}>
        {/* GPS Signal and Re-center - Above Start button */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center px-3 py-2 rounded-full bg-background/90 backdrop-blur-md shadow-md border border-border/30">
            {getSignalBars()}
          </div>
          
          {/* Re-center map button - only visible when user has scrolled away */}
          {currentPosition && !isMapCentered && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRecenterMap}
              className="h-10 w-10 rounded-full bg-primary/90 backdrop-blur-md shadow-lg border border-border/30 animate-in fade-in-0 zoom-in-95 duration-200"
            >
              <Navigation className="h-4 w-4 text-primary-foreground" />
            </Button>
          )}
        </div>
        
        {/* Start Button - Same style as Log Gym Workout */}
        <Button
          onClick={handleStart}
          disabled={isStarting}
          className="w-full h-14 text-lg font-bold bg-gradient-primary text-black shadow-glow hover:shadow-lg transition-all duration-300 rounded-2xl disabled:opacity-70"
        >
          {isStarting
            ? t('running.start.starting')
            : effectiveGpsKind === 'walk'
              ? t('running.start.startButtonWalk')
              : effectiveGpsKind === 'hike'
                ? t('running.start.startButtonHike')
                : t('running.start.startButton')}
        </Button>
      </div>

      {/* Countdown overlay: 3, 2, 1, Go! */}
      {showCountdown && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-md">
          <div className="text-[min(25vw,120px)] font-bold tabular-nums text-primary animate-in zoom-in-50 duration-300">
            {countdownNumber === 'go' ? t('running.start.countdownGo') : countdownNumber}
          </div>
        </div>
      )}

      {/* Location permission dialog - shown when run blocked due to missing Always permission */}
      <LocationPermissionDialog
        open={permissionDialog.open}
        onOpenChange={(open) => setPermissionDialog((p) => ({ ...p, open }))}
        reason={permissionDialog.reason}
        onOpenSettings={openLocationSettings}
      />
    </div>
  );
};
